import React from 'react';
import { useAppNavigation } from '../hooks/useAppNavigation';

const TermsOfService = () => {
  const navigate = useAppNavigation();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Terms of Service</h1>
            <button
              onClick={() => navigate.to.HOME()}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Back to App
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="mb-8">
            <p className="text-gray-600">
              <strong>Effective Date:</strong> October 9, 2024<br />
              <strong>Last Updated:</strong> October 9, 2024
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 mb-4">
              By using ClutterLogix, you agree to these Terms of Service and our Privacy Policy. 
              If you don't agree, please don't use our service.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">2. Our Service</h2>
            <p className="text-gray-700 mb-2">ClutterLogix is an AI-powered home inventory management platform that helps you:</p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Create visual inventories with photo uploads</li>
              <li>Generate automatic descriptions and tags using AI</li>
              <li>Search and organize your belongings</li>
              <li>Export your data in CSV format</li>
              <li>Share inventories with family members (Pro plan)</li>
              <li>Generate insurance and moving reports (Pro plan)</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">3. Account Registration</h2>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>You must provide accurate information when creating your account</li>
              <li>You're responsible for keeping your account secure</li>
              <li>One account per person - sharing accounts is not allowed</li>
              <li>By creating an account, you confirm you are at least 13 years old</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">4. Subscription Plans</h2>
            
            <div className="grid md:grid-cols-3 gap-6 my-6">
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-2">Free Plan - $0/month</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Unlimited items — free forever</li>
                  <li>• Voice descriptions & tags</li>
                  <li>• AI assist for your first 100 items</li>
                  <li>• CSV export</li>
                </ul>
              </div>
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-2">Plus Plan - $4.99/month</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• 5,000 items</li>
                  <li>• All Free features</li>
                </ul>
              </div>
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-2">Pro Plan - $9.99/month</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Unlimited items</li>
                  <li>• All Plus features</li>
                  <li>• Insurance reports</li>
                  <li>• Family sharing (up to 4 members)</li>
                </ul>
              </div>
            </div>

            <h4 className="font-semibold text-gray-900 mb-2">Billing:</h4>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Payments processed through Stripe</li>
              <li>Automatic recurring billing</li>
              <li>Cancel anytime</li>
              <li>Refunds handled case-by-case</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">5. Family Sharing (Pro Feature)</h2>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Pro users can create family groups with up to 4 members</li>
              <li>Family owners control access and permissions</li>
              <li>All family members must agree to these terms</li>
              <li>Family owners are responsible for their members' actions</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">6. Acceptable Use</h2>
            
            <h3 className="font-semibold text-gray-900 mb-2">You may:</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Use ClutterLogix for personal inventory management</li>
              <li>Share data with family members (with their consent)</li>
              <li>Export your data</li>
            </ul>

            <h3 className="font-semibold text-gray-900 mb-2">You may not:</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Upload illegal, harmful, or inappropriate content</li>
              <li>Try to hack or break our security</li>
              <li>Use the service for commercial purposes without permission</li>
              <li>Share your account with others</li>
              <li>Reverse engineer our software</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">7. Privacy</h2>
            <p className="text-gray-700 mb-4">
              Your privacy is important to us. Please read our{' '}
              <button
                onClick={() => navigate.to.PRIVACY_POLICY()}
                className="text-blue-600 hover:underline"
              >
                Privacy Policy
              </button>{' '}
              to understand how we collect, use, and protect your information.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">8. Contact Us</h2>
            <p className="text-gray-700 mb-2">Questions about these terms? Contact us at:</p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Email: support@clutterlogix.com</li>
              <li>Website: https://clutterlogix.com</li>
            </ul>

            <div className="mt-12 p-4 bg-gray-50 rounded-lg border-l-4 border-blue-500">
              <p className="text-sm text-gray-700 italic">
                By using ClutterLogix, you acknowledge that you've read and agree to these Terms of Service.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
